import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

import {
  Accordion,
  Banner,
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  CopyField,
  DataList,
  DataTable,
  Dialog,
  EmptyState,
  EntitlementGate,
  ErrorState,
  Field,
  Grid,
  IconButton,
  Input,
  MetricTile,
  PageHeader,
  Pagination,
  Panel,
  PanicButton,
  ProviderButton,
  QueueItem,
  QuotaMeter,
  Select,
  Separator,
  SessionStatus,
  Skeleton,
  Stack,
  StatusBadge,
  SurfaceSlot,
  Switch,
  Tabs,
  Toolbar,
  Textarea,
} from "@/components/ui";
import { canvasAddress, spaceAddress } from "@/lib/env";

export const Route = createFileRoute("/kit")({
  component: Kit,
});

/** One section of the gallery, so every component is looked at under its own name. */
function Case({ name, children }: { name: string; children: React.ReactNode }) {
  return (
    <Panel title={name}>
      <Stack gap={4}>{children}</Stack>
    </Panel>
  );
}

const ROWS = [
  { id: "1", space: "Friday Night Show", code: "SHORE-FRI", live: 2 },
  { id: "2", space: "Thursday Table", code: "HEARTH-THU", live: 0 },
  { id: "3", space: "Q3 All Hands", code: "NORTHRIG-Q3", live: 0 },
];

function Kit() {
  const [tab, setTab] = useState("one");
  const [page, setPage] = useState(1);
  const [dialog, setDialog] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [theme, setTheme] = useState<"dark" | "light">("dark");

  return (
    <div data-theme={theme} style={{ background: "var(--al-surface)", minHeight: "100dvh" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "var(--al-space-7)" }}>
        <Stack gap={6}>
          <PageHeader
            eyebrow="Design kit"
            title="Every component"
            description="One of each, at its real size, on real tokens."
            actions={
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              >
                {theme}
              </Button>
            }
          />

          <Grid minColumnWidth="320px" gap={5}>
            <Case name="Button">
              <Stack direction="row" gap={3} wrap>
                <Button variant="primary">Primary</Button>
                <Button variant="secondary">Secondary</Button>
                <Button variant="ghost">Ghost</Button>
                <Button variant="destructive">Destructive</Button>
              </Stack>
              <Stack direction="row" gap={3} wrap>
                <Button size="sm">Small</Button>
                <Button size="md">Medium</Button>
                <Button size="lg">Large</Button>
                <Button loading>Loading</Button>
                <Button disabled>Disabled</Button>
              </Stack>
            </Case>

            <Case name="IconButton">
              <Stack direction="row" gap={3}>
                <IconButton label="Close">x</IconButton>
                <IconButton label="Remove" tone="danger">
                  x
                </IconButton>
                <IconButton label="Big" size="lg">
                  +
                </IconButton>
              </Stack>
            </Case>

            <Case name="StatusBadge">
              <Stack direction="row" gap={3} wrap>
                <StatusBadge tone="neutral">stopped</StatusBadge>
                <StatusBadge tone="success">running</StatusBadge>
                <StatusBadge tone="warning">paused</StatusBadge>
                <StatusBadge tone="danger">failed</StatusBadge>
                <StatusBadge tone="info">loaded</StatusBadge>
              </Stack>
            </Case>

            <Case name="Banner">
              <Banner tone="info" title="Loaded" />
              <Banner tone="warning" title="One position open" />
              <Banner tone="danger" title="Refused" />
              <Banner tone="success" title="Paired" />
            </Case>

            <Case name="MetricTile">
              <Grid minColumnWidth="140px" gap={3}>
                <MetricTile value="Loaded" label="This account is running" hint="Unload to move it" />
                <MetricTile value="2" label="Activities running" hint="3 added" />
                <MetricTile value="148 / 200" label="Participants" />
              </Grid>
            </Case>

            <Case name="QuotaMeter">
              <QuotaMeter label="Participants at once" used={148} limit={200} />
              <QuotaMeter
                label="Fully scoped canvases"
                used={50}
                limit={50}
                exhaustedHint="Stop an activity to free capacity."
              />
            </Case>

            <Case name="SessionStatus">
              <SessionStatus state="live" detail="2 running" />
              <SessionStatus state="starting" detail="Friday Night Show" />
              <SessionStatus state="off" detail="nothing running" />
              <SessionStatus state="unknown" />
            </Case>

            <Case name="Toolbar">
              <Toolbar fill>
                <Input placeholder="Find an activity" aria-label="Find an activity" />
                <Button size="sm" variant="ghost">
                  Clear
                </Button>
              </Toolbar>
              <Toolbar>
                <Button size="sm" variant="ghost">
                  Versions
                </Button>
                <Button size="sm" variant="ghost">
                  Rule
                </Button>
              </Toolbar>
            </Case>

            <Case name="Field, Input, Textarea">
              <Field label="Space name">
                {(render) => <Input {...render} defaultValue="Friday Night Show" />}
              </Field>
              <Field label="Join code" description="Static">
                {(render) => <Input {...render} mono defaultValue="SHORE-FRI" />}
              </Field>
              <Field label="Message">{(render) => <Textarea {...render} counter />}</Field>
            </Case>

            <Case name="Select, Switch, Checkbox">
              <Select
                options={[
                  { value: "pinned", label: "pinned" },
                  { value: "latest", label: "latest" },
                ]}
                defaultValue="pinned"
              />
              <Switch label="Fog of war" checked />
              <Checkbox label="Hold submissions" description="Route through the queue" />
            </Case>

            <Case name="CopyField">
              <CopyField value={spaceAddress("SHORE-FRI")} label="join address" />
              <CopyField value="HGTD-4K9P" label="device code" group large />
            </Case>

            <Case name="Tabs">
              <Tabs
                label="Space"
                value={tab}
                onChange={setTab}
                tabs={[
                  { id: "one", label: "Control" },
                  { id: "two", label: "Canvases", count: 4 },
                  { id: "three", label: "Bindings" },
                ]}
              />
            </Case>

            <Case name="Accordion">
              <Accordion
                items={[
                  { id: "a", title: "Streaming Core", meta: "2.4.1", content: "Three members" },
                  { id: "b", title: "Game Show", meta: "1.0.3", content: "Five members" },
                ]}
              />
            </Case>

            <Case name="Separator">
              <Separator />
              <Separator label="or" />
            </Case>

            <Case name="Skeleton">
              <Skeleton width="60%" height="14px" />
              <Skeleton width="40%" height="14px" />
              <Skeleton width="32px" height="32px" circle />
            </Case>

            <Case name="ProviderButton">
              <ProviderButton provider="twitch" />
              <ProviderButton provider="google" action="Link" />
              <ProviderButton provider="discord" loading />
            </Case>

            <Case name="PanicButton">
              <PanicButton
                label="Clear every canvas"
                confirmLabel="Press again to clear"
                onConfirm={() => undefined}
              />
            </Case>

            <Case name="EntitlementGate">
              <EntitlementGate
                entitled={false}
                capability="effector.obs"
                action={<Button size="sm">See plans</Button>}
              />
            </Case>

            <Case name="EmptyState, ErrorState">
              <EmptyState headline="Nothing added" action={<Button size="sm">Add</Button>} />
              <ErrorState headline="Could not load" code="502" retryLabel="Retry" />
            </Case>

            <Case name="SurfaceSlot">
              <SurfaceSlot name="Vote Window" version="1.1.4" minHeight="120px">
                <Stack gap={2}>
                  <StatusBadge tone="success">running</StatusBadge>
                </Stack>
              </SurfaceSlot>
              <SurfaceSlot name="Room Pulse" version="0.9.2" state="empty" minHeight="80px" />
            </Case>

            <Case name="QueueItem">
              <QueueItem
                content="name the boat Unsinkable II"
                author="pipwright"
                activity="Vote Window"
                waiting="0:08"
              />
            </Case>

            <Case name="Dialogs">
              <Stack direction="row" gap={3}>
                <Button size="sm" onClick={() => setDialog(true)}>
                  Dialog
                </Button>
                <Button size="sm" variant="destructive" onClick={() => setConfirm(true)}>
                  Confirm
                </Button>
              </Stack>
              <Dialog
                open={dialog}
                title="Pair a canvas"
                description="Paste this into an OBS browser source."
                onClose={() => setDialog(false)}
                actions={<Button onClick={() => setDialog(false)}>Done</Button>}
              >
                <CopyField value={canvasAddress("SHORE-FRI", "broadcast")} label="canvas address" />
              </Dialog>
              <ConfirmDialog
                open={confirm}
                title="Archive this space?"
                confirmLabel="Archive"
                onConfirm={() => setConfirm(false)}
                onCancel={() => setConfirm(false)}
              />
            </Case>
          </Grid>

          <Panel title="DataTable">
            <DataTable
              caption="Spaces"
              density="comfortable"
              columns={[
                { key: "space", header: "Space", emphasis: true },
                { key: "code", header: "Join code", mono: true },
                { key: "live", header: "Running", numeric: true },
                {
                  key: "a",
                  header: "",
                  align: "right",
                  render: () => (
                    <Button size="sm" variant="ghost">
                      Open
                    </Button>
                  ),
                },
              ]}
              rows={ROWS}
            />
            <Pagination page={page} pageCount={4} pageSize={3} total={11} label="Page" onChange={setPage} />
          </Panel>

          <Panel title="DataTable, loading and empty" tone="sunken">
            <DataTable
              caption="Loading"
              columns={[
                { key: "space", header: "Space" },
                { key: "code", header: "Join code", mono: true },
              ]}
              rows={[]}
              loading
            />
            <DataTable
              caption="Empty"
              columns={[
                { key: "space", header: "Space" },
                { key: "code", header: "Join code", mono: true },
              ]}
              rows={[]}
              emptyHeadline="No spaces"
            />
          </Panel>

          <Panel title="DataList">
            <DataList
              items={[
                {
                  id: "one",
                  title: "STUDIO-PC",
                  badge: <StatusBadge tone="success">paired</StatusBadge>,
                  fields: [
                    { label: "OS", value: "Windows 11" },
                    { label: "Agent", value: "1.9.0", mono: true },
                  ],
                  action: (
                    <Button size="sm" variant="ghost">
                      Remove
                    </Button>
                  ),
                },
              ]}
            />
          </Panel>

          <Card title="Card">
            <Stack gap={3}>
              <StatusBadge tone="info">loaded</StatusBadge>
              <CopyField value={spaceAddress("SHORE-FRI")} label="join address" />
            </Stack>
          </Card>
        </Stack>
      </div>
    </div>
  );
}
